import {
  createLessonBlock,
  updateLessonBlock,
  deleteLessonBlock,
  updateLessonBlocksOrder,
} from '@/data/services/lessonBlocksServices';
import LessonBlock, { PickLessonBlockInfo } from '@/models/_deprecated/LessonBlock';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import { separeArrayStudioActions } from './info/CourseStudioInfo.hooks';
import { useCourseStudioLessons } from './matrix/CourseStudioLessons.hooks';
import { useCourseStudioBook } from './details/CourseStudioBook.hooks';
import { useCourseStudioChapters } from './details/CourseStudioChapters.hook';
import { useCourseStudioSubchapters } from './details/CourseStudioSubchapters.hooks';

async function performToDeleteBlocks(slug: string, blocks: LessonBlock[]) {
  const deletedBlocks: LessonBlock[] = [];

  for (const block of blocks) {
    await deleteLessonBlock(slug, block.id);
    deletedBlocks.push(block);
  }

  return deletedBlocks;
}

async function performUpdateBlock(
  slug: string,
  newBlock: TempLessonBlock,
  currentBlock: TempLessonBlock,
) {
  const blockId = newBlock.id;

  const changes = buildChangedObject(currentBlock, newBlock, {
    ignoreFields: ['tempId', 'lessons'],
  });

  if (!Object.keys(changes).length) return currentBlock;

  return await updateLessonBlock(slug, blockId, changes);
}

async function performToUpdateBlocks(
  slug: string,
  currentBlocks: TempLessonBlock[],
  newBlocks: TempLessonBlock[],
) {
  const updatedBlocks: TempLessonBlock[] = [];

  for (const block of newBlocks) {
    const currentBlock = currentBlocks.find(b => b.id === block.id);

    if (!currentBlock) continue;

    const updated = await performUpdateBlock(slug, block, currentBlock);

    updatedBlocks.push({
      ...updated,
      lessons: block.lessons,
    });
  }

  return updatedBlocks;
}

async function performToCreateBlocks(slug: string, blocks: TempLessonBlock[]) {
  const createdBlocks: TempLessonBlock[] = [];

  for (const block of blocks) {
    const { name, order } = block;

    const onlyBlockInfoNeeded: PickLessonBlockInfo = { name, order };

    const createdBlock = await createLessonBlock(slug, onlyBlockInfoNeeded);

    const createdBlockWithLessons = new TempLessonBlock({
      ...createdBlock,
      tempId: block.tempId,
      lessons: block.lessons,
    });

    createdBlocks.push(createdBlockWithLessons);
  }

  return createdBlocks;
}

function hasLessonsOrderChanged(
  existingCurrentBlocks: TempLessonBlock[],
  existingNewBlocks: TempLessonBlock[],
): boolean {
  const currentAllLessonIds: number[] = [];
  existingCurrentBlocks.forEach(block => {
    block.lessons.forEach(lesson => {
      if (lesson.id) {
        currentAllLessonIds.push(lesson.id);
      }
    });
  });

  const newAllLessonIds: number[] = [];
  existingNewBlocks.forEach(block => {
    block.lessons.forEach(lesson => {
      if (lesson.id) {
        newAllLessonIds.push(lesson.id);
      }
    });
  });

  if (currentAllLessonIds.length !== newAllLessonIds.length) {
    return true;
  }

  const globalOrderChanged = !newAllLessonIds.every(
    (id, index) => id === currentAllLessonIds[index],
  );

  if (globalOrderChanged) {
    return true;
  }

  const currentBlockLessonMap = new Map<number, number[]>();
  const newBlockLessonMap = new Map<number, number[]>();

  existingCurrentBlocks.forEach(block => {
    if (block.id) {
      currentBlockLessonMap.set(
        block.id,
        block.lessons.filter(lesson => lesson.id).map(lesson => lesson.id!),
      );
    }
  });

  existingNewBlocks.forEach(block => {
    if (block.id) {
      newBlockLessonMap.set(
        block.id,
        block.lessons.filter(lesson => lesson.id).map(lesson => lesson.id!),
      );
    }
  });

  for (const [blockId, currentLessonIds] of currentBlockLessonMap) {
    const newLessonIds = newBlockLessonMap.get(blockId);
    if (!newLessonIds) {
      return true;
    }

    if (currentLessonIds.length !== newLessonIds.length) {
      return true;
    }

    for (let i = 0; i < currentLessonIds.length; i++) {
      if (currentLessonIds[i] !== newLessonIds[i]) {
        return true;
      }
    }
  }

  for (const [blockId] of newBlockLessonMap) {
    if (!currentBlockLessonMap.has(blockId)) {
      return true;
    }
  }

  return false;
}

function hasOrderChanged(
  currentBlocks: TempLessonBlock[],
  newBlocks: TempLessonBlock[],
): boolean {
  const existingNewBlocks = newBlocks.filter(block => block.id);
  const existingCurrentBlocks = currentBlocks.filter(block => block.id);

  if (existingNewBlocks.length === 0) return false;

  if (existingNewBlocks.length !== existingCurrentBlocks.length) {
    return true;
  }

  const newOrderIds = existingNewBlocks.map(block => block.id);
  const currentOrderIds = existingCurrentBlocks.map(block => block.id);

  const blocksOrderChanged = !newOrderIds.every(
    (id, index) => id === currentOrderIds[index],
  );

  if (blocksOrderChanged) {
    return true;
  }

  const lessonsOrderChanged = hasLessonsOrderChanged(
    existingCurrentBlocks,
    existingNewBlocks,
  );

  return lessonsOrderChanged;
}

function buildBlocksAfterCRUD(
  newBlocks: TempLessonBlock[],
  blocksFromCreate: TempLessonBlock[],
  blocksFromUpdate: TempLessonBlock[],
): TempLessonBlock[] {
  return newBlocks.map((block, index) => {
    let resultBlock: TempLessonBlock;

    if (block.id) {
      const updatedBlock = blocksFromUpdate.find(b => b.id === block.id);
      resultBlock = updatedBlock || block;
    } else {
      const createdBlock = blocksFromCreate.find(
        b => b.tempId === block.tempId,
      );
      resultBlock = createdBlock || block;
    }

    return {
      ...resultBlock,
      order: index + 1,
    };
  });
}

export async function performToReorderBlocks(
  slug: string,
  blocksToReorder: TempLessonBlock[],
) {
  const blocksForReorder = blocksToReorder
    .filter(block => block.id)
    .map(block => ({
      id: block.id,
      lessonsIds: block.lessons
        .filter(lesson => lesson.id)
        .map(lesson => ({ id: lesson.id })),
    }));

  const blocksForReorderWithLessonsIds = blocksForReorder.map(block => ({
    id: block.id,
    lessonsIds: block.lessonsIds,
  }));

  await updateLessonBlocksOrder(slug, blocksForReorderWithLessonsIds);
}

export function useCourseStudioBlocks() {
  const { handleLessonOperations } = useCourseStudioLessons();

  const { handleCreateBooks, handleUpdateBooks } = useCourseStudioBook();

  const { handleCreateChapters, handleUpdateChapters } =
    useCourseStudioChapters();

  const { handleCreateSubchapters, handleUpdateSubchapters } =
    useCourseStudioSubchapters();

  async function handleCreateBlocks(
    slug: string,
    newBlocks: TempLessonBlock[],
  ): Promise<TempLessonBlock[]> {
    const createdBlocks: TempLessonBlock[] = await performToCreateBlocks(
      slug,
      newBlocks,
    );

    const blocksWithLessons = await handleLessonOperations(
      slug,
      [],
      createdBlocks,
    );

    const blocksWithBooks = await handleCreateBooks(blocksWithLessons);

    const blocksWithChapters = await handleCreateChapters(blocksWithBooks);

    const blocksWithSubchapters = await handleCreateSubchapters(
      blocksWithChapters,
    );

    return blocksWithSubchapters;
  }

  async function handleUpdateBlocks(
    slug: string,
    currentBlocks: TempLessonBlock[],
    newBlocks: TempLessonBlock[],
  ) {
    const { arrayToDelete, arrayToUpdate, arrayToCreate } =
      separeArrayStudioActions(currentBlocks, newBlocks);

    await performToDeleteBlocks(slug, arrayToDelete);
    const blocksFromUpdate = await performToUpdateBlocks(
      slug,
      currentBlocks,
      arrayToUpdate,
    );
    const blocksFromCreate = await performToCreateBlocks(slug, arrayToCreate);

    const blocksAfterCRUD = buildBlocksAfterCRUD(
      newBlocks,
      blocksFromCreate,
      blocksFromUpdate,
    );

    const blocksWithUpdatedLessons = await handleLessonOperations(
      slug,
      currentBlocks,
      blocksAfterCRUD,
    );

    const blocksWithUpdatedBooks = await handleUpdateBooks(
      currentBlocks,
      blocksWithUpdatedLessons,
    );

    const blocksWithUpdatedChapters = await handleUpdateChapters(
      currentBlocks,
      blocksWithUpdatedBooks,
    );

    const blocksWithUpdatedSubchapters = await handleUpdateSubchapters(
      currentBlocks,
      blocksWithUpdatedChapters,
    );

    const orderChanged = hasOrderChanged(
      currentBlocks,
      blocksWithUpdatedSubchapters,
    );

    if (orderChanged) {
      await performToReorderBlocks(slug, blocksWithUpdatedSubchapters);
    }

    return blocksWithUpdatedSubchapters;
  }

  return {
    handleUpdateBlocks,
    handleCreateBlocks,
  };
}
